import axios from 'axios'
import { API_BASE_URL } from 'configs/AppConfig'
import store from '../store'
import { signOutSuccess } from 'store/slices/authSlice'
import {
  AUTH_TOKEN,
  AUTH_SESSION_EXPIRY,
  AUTH_DEVICE_TOKEN,
} from 'constants/AuthConstant'
// import FirebaseService from 'services/FirebaseService'
import Utils from 'utils'
// import { currentUser } from './FirebaseAuth'
import { notifyError } from 'utils/notificationBridge'

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
})

// Config
const TOKEN_PAYLOAD_KEY = 'authorization'
const PUBLIC_REQUEST_KEY = 'public-request'

const clearLocalAuth = () => {
  localStorage.removeItem(AUTH_TOKEN)
  localStorage.removeItem(AUTH_SESSION_EXPIRY)
}

// Redux-driven sign-out — ProtectedRoute reacts to cleared `state.auth.token`
// and Navigates to /auth/login (no full reload → no flash).
const signOutClientSide = () => {
  clearLocalAuth()
  store.dispatch(signOutSuccess())
}

// Utility function to check inactivity
let isInActive = false
let activityTimeout
// Prevents concurrent 401/403 responses from each triggering a separate
// sign-out flow (and separate notifications) at the same time. Sticks
// true for the rest of this auth cycle — reset only when the user signs
// back in (token transitions from falsy to truthy in Redux).
let isHandling401 = false

// Proactive session-expiry auto-logout. `sessionExpiry` is epoch ms
// (stringified) returned by login API. Timer fires exactly at expiry,
// without waiting for the next API call to 401.
let sessionExpiryTimeout = null

const disarmSessionExpiryTimer = () => {
  if (sessionExpiryTimeout) {
    clearTimeout(sessionExpiryTimeout)
    sessionExpiryTimeout = null
  }
}

const armSessionExpiryTimer = (expiry) => {
  disarmSessionExpiryTimer()
  if (!expiry) return
  const expiryMs = Number(expiry)
  if (!Number.isFinite(expiryMs)) return
  const remaining = expiryMs - Date.now()
  const fireExpiry = () => {
    notifyError({
      title: 'Session expired',
      description: 'Please login again',
    })
    signOutClientSide()
  }
  if (remaining <= 0) {
    fireExpiry()
    return
  }
  sessionExpiryTimeout = setTimeout(fireExpiry, remaining)
}

// Defer subscription setup — `store` is imported circularly via
// authSlice/userService, so it's still in TDZ during this module's
// top-level evaluation. Run after the current microtask flush.
let prevAuthToken = null
let prevSessionExpiry = null
setTimeout(() => {
  const initial = store.getState()?.auth
  prevAuthToken = initial?.token || null
  prevSessionExpiry = initial?.sessionExpiry || null
  if (prevAuthToken && prevSessionExpiry) {
    armSessionExpiryTimer(prevSessionExpiry)
  }
  store.subscribe(() => {
    const next = store.getState()?.auth
    const t = next?.token || null
    const e = next?.sessionExpiry || null

    if (t && !prevAuthToken) {
      // re-login → reset dedup flag
      isHandling401 = false
    }
    if (!t && prevAuthToken) {
      // logout → disarm expiry timer
      disarmSessionExpiryTimer()
    }
    if (t && e && e !== prevSessionExpiry) {
      // login or expiry changed → arm/re-arm
      armSessionExpiryTimer(e)
    }
    prevAuthToken = t
    prevSessionExpiry = e
  })
}, 0)

const resetInactivity = () => {
  isInActive = false
  clearTimeout(activityTimeout)
  if (!localStorage.getItem(AUTH_TOKEN)) return
  activityTimeout = setTimeout(() => {
    isInActive = true
    notifyError({
      title: 'Authentication Fail',
      description: 'Please login again',
    })
    signOutClientSide()
  }, 3600000) // 1 hour inactivity timeout
}

// Add event listeners for user activity
['mousemove', 'keypress', 'mousedown', 'scroll', 'touchstart'].forEach((event) => {
  window.addEventListener(event, resetInactivity)
})

// Initial inactivity setup
resetInactivity()

// API Request interceptor
service.interceptors.request.use(
  async (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN)
    const isPublicRequest = config.headers?.[PUBLIC_REQUEST_KEY]

    // const expiresAtRaw = localStorage.getItem('expiresAt')
    // const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : 0
    // const isTokenExpired = Boolean(expiresAt) && expiresAt < Date.now()
    // let tokenForApi;
    // if (isTokenExpired)
    //   tokenForApi = await FirebaseService.refreshToken()
    // else tokenForApi = jwtToken

    let tokenForApi = config.tokenFromApi || jwtToken

    // if (config.tokenFromApi) {
    //   tokenForApi = config.tokenFromApi
    // } else if (isTokenExpired && !isInActive) {
    //   try {
    //     tokenForApi = await FirebaseService.refreshToken()
    //   } catch (error) {
    //     console.error('Token refresh failed', error)
    //   }
    // } else tokenForApi = jwtToken

    config.headers = config.headers || {}

    if (tokenForApi) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${tokenForApi}`
      config.headers.deviceToken =
        window.localStorage.getItem(AUTH_DEVICE_TOKEN)
    }

    if (!jwtToken && !isPublicRequest) {
      const onAuthRoute = window.location.pathname.startsWith('/auth')
      if (!onAuthRoute) {
        signOutClientSide()
      }
      return Promise.reject(new Error('Unauthorized request'))
    }

    return config
  },
  (error) => {
    console.log('Thrown Error', error);
    // Do something with request error here
    notifyError({
      title: 'Error',
    })
    return Promise.reject(error)
  }
)

// API respone interceptor
service.interceptors.response.use(
  (response) => {
    return response.data

  },
  async (error) => {
    console.log('ERROR OCCURED', error)

    // The requesting service set silent:true on this config — skip all side-effects.
    if (error?.config?.silent) return Promise.reject(error)

    let notificationParam = {
      message: '',
    }
    const status = error?.response?.status
    const responseData = error?.response?.data

    // Remove token and redirect
    if (status === 401 || status === 403) {
      // Multiple concurrent requests can all receive 401 at the same time.
      // Only the first one should run the sign-out flow and show the notification.
      if (isHandling401) return Promise.reject(error)
      isHandling401 = true
      isInActive = true
      notifyError({
        title: 'Authentication Fail',
        description: 'Please login again',
      })
      // Server already invalidated the session (hence 401). Skip the
      // server sign-out call — just clear client state and let
      // ProtectedRoute redirect via Redux. The `isHandling401` flag
      // is reset on the next successful sign-in (store subscription).
      signOutClientSide()
      return Promise.reject(error)
    }

    if (!status) {
      notifyError({
        title: 'Network Error',
        description: error?.message || 'Request failed. Please try again.',
      })
      return Promise.reject(error)
    }

    if (status === 400) {
      notificationParam.message = responseData?.message
    }
    if (status === 409) {
      notificationParam.message = responseData?.message
    }

    if (status === 404) {
      notificationParam.message = responseData?.message || 'Not Found'
    }

    if (status === 500) {
      notificationParam.message =
        responseData?.message || 'Something went wrong. Please try again.'
    }

    if (status === 508) {
      notificationParam.message = 'Time Out'
    }

    if(responseData?.message) {
      notificationParam.message = responseData?.message
    }
    notifyError(notificationParam)
    if (responseData) {
      Utils.errorValidator(responseData)
    }

    return Promise.reject(error)
  }
)

export default service
